import { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader, Kpi, Empty } from "@/components/ui-bits";
import { toast } from "sonner";
import { ArrowClockwise, TrendUp, EnvelopeOpen, CursorClick } from "@phosphor-icons/react";

const TEMP_CLS = {
  Cold: "bg-slate-100 text-slate-700",
  Warm: "bg-amber-100 text-amber-800",
  Hot: "bg-orange-100 text-orange-800",
  "High Intent": "bg-rose-100 text-rose-800",
  "Sales Ready": "bg-emerald-100 text-emerald-800",
};
const CHANNEL_CLS = "bg-[#0B3D91]/10 text-[#0B3D91]";

export default function LeadIntel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get("/leads/analytics").then((r) => setData(r.data))
      .catch(() => toast.error("Could not load analytics"))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const t = data?.totals || {};
  const e = data?.email || {};
  const temp = data?.temperature || {};
  const maxCh = Math.max(1, ...(data?.by_channel || []).map((c) => c.leads));

  return (
    <div data-testid="lead-intel-page">
      <PageHeader title="Sales Intelligence" subtitle="Lead scoring, funnel and marketing attribution from your RecoverOS website">
        <button onClick={load} className="inline-flex items-center gap-1 h-9 px-3 text-sm rounded-sm border border-border hover:border-[#0B3D91]/40" data-testid="lead-intel-refresh">
          <ArrowClockwise size={15} /> Refresh
        </button>
      </PageHeader>

      {loading ? (
        <div className="p-10 text-center text-muted-foreground">Loading analytics…</div>
      ) : !data ? (
        <Empty text="No analytics yet." />
      ) : (
        <>
          {/* SALES DASHBOARD */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
            <Kpi label="Total Leads" value={t.total_leads} testId="li-total" />
            <Kpi label="New Today" value={t.new_today} accent="text-[#0B3D91]" testId="li-new-today" />
            <Kpi label="High Intent" value={t.high_intent} accent="text-rose-600" testId="li-high-intent" />
            <Kpi label="Sales Ready" value={t.sales_ready} accent="text-emerald-600" testId="li-sales-ready" />
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <Kpi label="Demo Requests" value={t.demo_requests} testId="li-demo" />
            <Kpi label="Registered / Trial" value={t.registered} accent="text-emerald-600" testId="li-registered" />
            <Kpi label="Customers" value={t.customers} accent="text-emerald-700" testId="li-customers" />
            <Kpi label="Conversion Rate" value={`${t.conversion_rate}%`} accent="text-[#16A34A]" testId="li-conv" />
          </div>

          {/* EMAIL METRICS */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <Kpi label="Emails Sent" value={e.sent} testId="li-emails-sent" />
            <Kpi label="Open Rate" value={`${e.open_rate}%`} accent="text-[#0B3D91]" testId="li-open-rate" />
            <Kpi label="Click Rate" value={`${e.click_rate}%`} accent="text-rose-600" testId="li-click-rate" />
            <Kpi label="Avg Lead Score" value={t.avg_score} testId="li-avg-score" />
          </div>

          <div className="grid lg:grid-cols-2 gap-6">
            {/* TEMPERATURE BREAKDOWN */}
            <div className="border border-border bg-card p-5" data-testid="li-temperature">
              <div className="label-caps mb-4 flex items-center gap-2"><TrendUp size={14} /> Lead Temperature</div>
              <div className="space-y-2.5">
                {["Sales Ready", "High Intent", "Hot", "Warm", "Cold"].map((k) => (
                  <div key={k} className="flex items-center gap-3">
                    <span className={`w-24 text-xs font-semibold px-2 py-0.5 rounded-sm text-center ${TEMP_CLS[k]}`}>{k}</span>
                    <div className="flex-1 h-2 bg-secondary rounded-full overflow-hidden">
                      <div className="h-full bg-[#0B3D91]" style={{ width: `${(temp[k] || 0) / Math.max(1, t.total_leads) * 100}%` }} />
                    </div>
                    <span className="w-8 text-right text-sm font-bold mono">{temp[k] || 0}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* MARKETING ATTRIBUTION */}
            <div className="border border-border bg-card p-5" data-testid="li-attribution">
              <div className="label-caps mb-4">Marketing Attribution — Leads by Source</div>
              {(data.by_channel || []).length === 0 ? (
                <div className="text-sm text-muted-foreground">No attribution data yet.</div>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="label-caps text-left border-b border-border">
                      <th className="pb-2">Channel</th><th className="pb-2 text-right">Leads</th>
                      <th className="pb-2 text-right">High Intent</th><th className="pb-2 text-right">Customers</th>
                      <th className="pb-2 text-right">Conv %</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.by_channel.map((c, i) => (
                      <tr key={c.channel} className="border-b border-border/60 last:border-0" data-testid={`li-channel-${i}`}>
                        <td className="py-2">
                          <span className={`text-xs font-semibold px-2 py-0.5 rounded-sm ${CHANNEL_CLS}`}>{c.channel}</span>
                          <div className="mt-1 h-1.5 bg-secondary rounded-full overflow-hidden" style={{ maxWidth: 140 }}>
                            <div className="h-full bg-[#16A34A]" style={{ width: `${c.leads / maxCh * 100}%` }} />
                          </div>
                        </td>
                        <td className="py-2 text-right font-bold mono">{c.leads}</td>
                        <td className="py-2 text-right mono text-rose-600">{c.high_intent}</td>
                        <td className="py-2 text-right mono text-emerald-700">{c.converted}</td>
                        <td className="py-2 text-right font-bold mono text-[#16A34A]">{c.conversion}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
